import { useState, useEffect } from "react";
import axios from "axios";
import { Container, Form, Button, Row, Col, Card, Spinner, Badge } from "react-bootstrap";

const API_URL = "https://madhusewingmachines.com/api/banner"; 

const BannerManagement = () => {
    const [bannerImage, setBannerImage] = useState(null);
    const [bannerTitle, setBannerTitle] = useState("");
    const [bannerDescription, setBannerDescription] = useState(""); 
    const [textColor, setTextColor] = useState("#000000");
    const [previewImage, setPreviewImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [banners, setBanners] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [editingBannerId, setEditingBannerId] = useState(null);

    // Fetch all banners on component mount
    useEffect(() => {
        fetchBanners();
    }, []);

    // Debug: Log when banners state changes
    useEffect(() => {
        console.log("Banners state updated:", banners);
        console.log("Banners count in state:", banners.length);
    }, [banners]);

    const fetchBanners = async () => {
        try {
            const response = await axios.get(API_URL);
            console.log("Raw response:", response);
            console.log("Response data:", response.data);
            console.log("Is Array?", Array.isArray(response.data));
            console.log("Type:", typeof response.data);
            console.log("Length:", response.data?.length);
            
            if (response.data) {
                const bannersArray = Array.isArray(response.data) ? response.data : [response.data];
                console.log("Banners array to set:", bannersArray);
                console.log("Banners array length:", bannersArray.length);
                setBanners(bannersArray);
                console.log("Total banners set:", bannersArray.length);
            } else {
                console.log("No data received, setting empty array");
                setBanners([]);
            }
        } catch (error) {
            console.error("Error fetching banners:", error);
            console.error("Error details:", error.response?.data);
            setBanners([]);
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setBannerImage(file);
            setPreviewImage(URL.createObjectURL(file));
        }
    };

    const handleSaveBanner = async () => {
        if (!bannerTitle.trim()) {
            alert("Please enter a banner title.");
            return;
        }

        if (!editMode && !bannerImage) {
            alert("Please select an image.");
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append("title", bannerTitle);
        formData.append("description", bannerDescription);
        formData.append("textColor", textColor);
        if (bannerImage) formData.append("image", bannerImage);

        try {
            if (editMode && editingBannerId) {
                await axios.put(`${API_URL}/${editingBannerId}`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                alert("Banner updated successfully!");
            } else {
                await axios.post(`${API_URL}/postbanner`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                alert("Banner added successfully!");
            }

            // Reset fields
            resetForm();
            fetchBanners();
        } catch (error) {
            console.error("Error saving banner:", error);
            alert("Failed to save banner.");
        } finally {
            setLoading(false);
        }
    };

    const handleEditBanner = (banner) => {
        setEditMode(true);
        setEditingBannerId(banner._id);
        setBannerTitle(banner.title);
        setBannerDescription(banner.description || "");
        setTextColor(banner.textColor || "#000000");
        setPreviewImage(`https://madhusewingmachines.com/uploads/${banner.image}`);
    };

    const handleDeleteBanner = async (bannerId) => {
        if (!window.confirm("Are you sure you want to delete this banner?")) return;

        try {
            await axios.delete(`${API_URL}/delete/${bannerId}`);
            alert("Banner deleted successfully!");
            fetchBanners();
        } catch (error) {
            console.error("Error deleting banner:", error);
            alert("Failed to delete banner.");
        }
    };

    const resetForm = () => {
        setBannerImage(null);
        setPreviewImage(null);
        setBannerTitle("");
        setBannerDescription("");
        setTextColor("#000000");
        setEditMode(false);
        setEditingBannerId(null);
    };

    return (
        <Container className="py-4">
            <h1 className="fw-bold mb-4 text-center">Banner Management</h1>

            {/* Add/Edit Banner Form */}
            <Card className="p-4 shadow-sm mb-4">
                <h5 className="mb-3">{editMode ? "Edit Banner" : "Add New Banner"}</h5>
                <Row className="mb-3">
                    <Col md={3}>
                        <Form.Label>Banner Image</Form.Label>
                        <Form.Control type="file" accept="image/*" onChange={handleImageUpload} />
                    </Col>
                    <Col md={3}>
                        <Form.Label>Banner Title</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter Banner Title"
                            value={bannerTitle}
                            onChange={(e) => setBannerTitle(e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Label>Banner Description</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter Banner Description"
                            value={bannerDescription}
                            onChange={(e) => setBannerDescription(e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Label>Text Color</Form.Label>
                        <div className="d-flex gap-2 align-items-center">
                            <Form.Control
                                type="color"
                                value={textColor}
                                onChange={(e) => setTextColor(e.target.value)}
                                style={{ width: '60px', height: '38px' }}
                            />
                            <Form.Control
                                type="text"
                                value={textColor}
                                onChange={(e) => setTextColor(e.target.value)}
                                placeholder="#000000"
                                style={{ flex: 1 }}
                            />
                        </div>
                    </Col>
                </Row>

                <div className="text-end">
                    {editMode && (
                        <Button variant="secondary" className="me-2" onClick={resetForm}>
                            Cancel
                        </Button>
                    )}
                    <Button variant="primary" onClick={handleSaveBanner} disabled={loading}>
                        {loading ? <Spinner size="sm" animation="border" /> : editMode ? "Update Banner" : "Add Banner"}
                    </Button>
                </div>

                {/* Preview */}
                {previewImage && (
                    <Card className="mt-3">
                        <Card.Img
                            variant="top"
                            src={previewImage}
                            alt="Banner Preview"
                            style={{ height: "200px", objectFit: "cover" }}
                            onError={(e) => {
                                console.error("Preview image failed to load:", e);
                                e.target.src = "https://via.placeholder.com/800x400?text=Preview+Image";
                                e.target.onerror = null;
                            }}
                        />
                        <Card.Body className="text-center">
                            <Card.Title>{bannerTitle}</Card.Title>
                            <Card.Text>{bannerDescription}</Card.Text>
                            <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                                <span>Text Color:</span>
                                <div 
                                    style={{ 
                                        width: '30px', 
                                        height: '30px', 
                                        backgroundColor: textColor,
                                        border: '1px solid #ccc',
                                        borderRadius: '4px'
                                    }}
                                />
                                <span>{textColor}</span>
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </Card>

            {/* Display All Banners */}
            <h4 className="mb-3">All Banners ({banners.length})</h4>
            <Row>
                {banners.length === 0 ? (
                    <Col>
                        <Card className="text-center p-4">
                            <Card.Body>
                                <p className="text-muted">No banners available. Add your first banner above!</p>
                            </Card.Body>
                        </Card>
                    </Col>
                ) : (
                    banners.map((banner) => (
                        <Col md={6} lg={4} key={banner._id} className="mb-4">
                            <Card className="shadow-sm h-100">
                                <Card.Img
                                    variant="top"
                                    src={`https://madhusewingmachines.com/uploads/${banner.image}`}
                                    alt={banner.title}
                                    style={{ height: "200px", objectFit: "cover" }}
                                    onError={(e) => {
                                        console.error("Image failed to load:", banner.image);
                                        console.error("Full URL:", e.target.src);
                                        e.target.src = "https://via.placeholder.com/800x400?text=Image+Not+Found";
                                        e.target.onerror = null;
                                    }}
                                />
                                <Card.Body>
                                    <Card.Title className="d-flex justify-content-between align-items-center">
                                        {banner.title}
                                        {banner.isActive && <Badge bg="success">Active</Badge>}
                                    </Card.Title>
                                    <Card.Text className="text-muted">{banner.description}</Card.Text>
                                    <div className="d-flex gap-2">
                                        <Button 
                                            variant="warning" 
                                            size="sm" 
                                            onClick={() => handleEditBanner(banner)}
                                        >
                                            Edit
                                        </Button>
                                        <Button 
                                            variant="danger" 
                                            size="sm" 
                                            onClick={() => handleDeleteBanner(banner._id)}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))
                )}
            </Row>
        </Container>
    );
};

export default BannerManagement;
